import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {AwardedOffersService, AwardedContractsStatistics} from '../../services/awarded-offers/awarded-offers.service';
import { ToastService } from '../../../main/services';
import {Offer} from "../../models/offer";
import moment from 'moment';
import {SettingService} from "../../../account/services";
import {DateTimeHelper} from "../../../common/helpers/datetime/datetime.helper";
import {
    ApexNonAxisChartSeries,
    ApexChart,
    ApexResponsive,
    ApexLegend,
    ApexDataLabels,
    ApexPlotOptions,
} from 'ng-apexcharts';

export type DonutChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    colors: string[];
    responsive: ApexResponsive[];
    legend: ApexLegend;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
};

export interface DatePreset {
    key: string;
    label: string;
    dateStart?: string;
    dateEnd?: string;
}

@Component({
    selector: 'jft-awarded-contracts',
    templateUrl: './awarded-contracts.component.html',
    styleUrls: ['./awarded-contracts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AwardedContractsComponent implements OnInit {
    public totalPageItems: number;
    public currentPage: number = 1;
    // Stats summary card is collapsed by default on mobile (toggled by the user).
    public statsOpen: boolean = false;
    public awarded_offers: Offer[] = [];
    public orderField: string;
    public order: string;
    public term: string;
    public dateStart: string;
    public dateEnd: string;
    public dateFormat: string = 'YYYY-MM-DD';
    public filterStatus = 'all';
    public defaultDateFormat: string = 'dd-MM-yyyy';
    public companySettings;
    public exporting = false;
    public loadingMore = false;

    public stats: AwardedContractsStatistics;
    public chartOptions: DonutChartOptions;

    public selectedPreset: string = '';
    public datePresetLabel: string = 'Date Range';
    public datePresets: DatePreset[] = [];

    constructor(
        private service: AwardedOffersService,
        private settingsService: SettingService,
        protected toast: ToastService,
    ) {
    }

    public ngOnInit() {
        this.buildDatePresets();
        this.updateData();
        this.loadStatistics();

        this.settingsService.fetch().subscribe((settings) => {
            this.companySettings = settings;
            if (this.companySettings.date_format){
                this.defaultDateFormat = DateTimeHelper.dateFormatToLuxFormat(this.companySettings.date_format);
            }
        });
    }

    private buildDatePresets() {
        const now = moment();
        const startOfYear = moment().startOf('year');
        const endOfYear = moment().endOf('year');
        const startOfLastYear = moment().subtract(1, 'year').startOf('year');
        const endOfLastYear = moment().subtract(1, 'year').endOf('year');

        this.datePresets = [
            { key: 'all', label: 'All Time' },
            {
                key: 'this_year',
                label: 'This Year',
                dateStart: startOfYear.format(this.dateFormat),
                dateEnd: endOfYear.format(this.dateFormat),
            },
            {
                key: 'last_year',
                label: 'Last Year',
                dateStart: startOfLastYear.format(this.dateFormat),
                dateEnd: endOfLastYear.format(this.dateFormat),
            },
            {
                key: 'last_6_months',
                label: 'Last 6 Months',
                dateStart: moment().subtract(6, 'months').format(this.dateFormat),
                dateEnd: now.format(this.dateFormat),
            },
            {
                key: 'next_6_months',
                label: 'Next 6 Months',
                dateStart: now.format(this.dateFormat),
                dateEnd: moment().add(6, 'months').format(this.dateFormat),
            },
            {
                key: 'next_12_months',
                label: 'Next 12 Months',
                dateStart: now.format(this.dateFormat),
                dateEnd: moment().add(12, 'months').format(this.dateFormat),
            },
            { key: 'custom', label: 'Custom Range' },
        ];
    }

    public applyDatePreset(preset: DatePreset) {
        this.selectedPreset = preset.key;

        if (preset.key === 'all') {
            this.dateStart = '';
            this.dateEnd = '';
            this.datePresetLabel = 'Date Range';
        } else if (preset.key === 'custom') {
            this.dateStart = '';
            this.dateEnd = '';
            this.datePresetLabel = 'Custom Range';
            return;
        } else {
            this.dateStart = preset.dateStart;
            this.dateEnd = preset.dateEnd;
            this.datePresetLabel = preset.label;
        }

        this.updateData();
    }

    public clearDateFilter() {
        this.dateStart = '';
        this.dateEnd = '';
        this.selectedPreset = '';
        this.datePresetLabel = 'Date Range';
        this.updateData();
    }

    private loadStatistics() {
        this.service.fetchStatistics().subscribe((stats) => {
            this.stats = stats;
            this.buildChart();
        });
    }

    private buildChart() {
        if (!this.stats) return;

        const hasData = this.stats.not_started + this.stats.active + this.stats.ended > 0;
        if (!hasData) return;

        this.chartOptions = {
            series: [this.stats.active, this.stats.not_started, this.stats.ended],
            chart: {
                width: 140,
                height: 140,
                type: 'donut',
                sparkline: {
                    enabled: false
                }
            },
            labels: ['Active', 'Not Started', 'Ended'],
            colors: ['#198754', '#0d6efd', '#6c757d'],
            plotOptions: {
                pie: {
                    donut: {
                        size: '55%',
                        labels: {
                            show: true,
                            name: {
                                fontSize: '10px'
                            },
                            value: {
                                fontSize: '14px'
                            },
                            total: {
                                show: true,
                                label: 'Total',
                                fontSize: '10px',
                                formatter: () => this.stats.total.toString()
                            }
                        }
                    }
                }
            },
            dataLabels: {
                enabled: false
            },
            legend: {
                show: false
            },
            responsive: [{
                breakpoint: 480,
                options: {
                    chart: { width: 200 }
                }
            }]
        };
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();
        if (this.orderField == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'ASC';
        }
        this.orderField = field;
        this.updateData();
    }

    public getOrderStr(field: string, direction = 'ASC') {
        if (direction != 'ASC') {
            field = `-${field}`;
        }
        return field;
    }

    public search() {
        if (this.term.length < 3 && this.term.length !== 0){
            return
        }
        this.updateData();
    }

    private updateData() {
        this.currentPage = 1;
        this.service.fetch(this.term, this.getOrderStr(this.orderField, this.order), this.dateStart, this.dateEnd,
            this.filterStatus, this.currentPage).subscribe((offers) => {
            this.awarded_offers = offers;
            this.totalPageItems = this.service.getTotalPageItems();
        }, (error) => {
            this.toast.error(error.error);
        });
    }

    public loadMore() {
        this.loadingMore = true;
        this.currentPage++;
        this.service.fetch(this.term, this.getOrderStr(this.orderField, this.order), this.dateStart, this.dateEnd,
            this.filterStatus, this.currentPage).subscribe((offers) => {
            this.awarded_offers = [...this.awarded_offers, ...offers];
            this.totalPageItems = this.service.getTotalPageItems();
            this.loadingMore = false;
        }, (error) => {
            this.loadingMore = false;
            this.toast.error(error.error);
        });
    }

    public clearTerm() {
        delete this.term;
        this.updateData();
    }

    public onDateStart(date) {
        this.dateStart = this.toStringDate(date);
        this.onDate();
    }

    public onDateEnd(date) {
        this.dateEnd = this.toStringDate(date);
        this.onDate();
    }

    private toStringDate(date) {
        if (!date){
            return
        }

        let month = date.month.toString();
        let day = date.day.toString();

        month = month.length == 1 ? '0' + month : month;
        day = day.length == 1 ? '0' + day : day;

        let dateOut = date.year.toString() + month + day;

        return moment.parseZone(dateOut).format(this.dateFormat);
    }

    public onDate() {
        this.updateData();
    }

    public changeFilter(term){
        this.filterStatus = term;
        this.updateData();
    }

    public onTouchedDate(){
        if (!this.dateEnd && !this.dateStart){
            this.updateData();
        }
    }

    public exportExcel() {
        this.exporting = true;
        this.service.exportExcel(
            this.term,
            this.getOrderStr(this.orderField, this.order),
            this.dateStart,
            this.dateEnd,
            this.filterStatus,
        ).subscribe((blob) => {
            this.exporting = false;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'Awarded Contracts.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        }, (error) => {
            this.exporting = false;
            this.toast.error('Failed to export contracts');
        });
    }
}
